import Matter from 'matter-js'
import MatterRope from './MatterRope'
import Consts from './MatterConsts'

interface ICornerBodies {
  topLeft: Matter.Body,
  topRight: Matter.Body,
  bottomLeft: Matter.Body,
  bottomRight: Matter.Body
}
interface ICornerPoints {
  topLeft: Matter.Vector,
  topRight: Matter.Vector,
  bottomLeft: Matter.Vector,
  bottomRight: Matter.Vector
}

export default class MatterCup {
  private cup: Matter.Body
  private cupCorners: ICornerBodies
  private rope: MatterRope
  private _waterLevel: number = 0.4
  private _waterLeftEdge: Matter.Body
  private _waterRightEdge: Matter.Body
  private isCaped: boolean = false

  constructor(world: Matter.World, width: number = 250, height: number = 350) {
    const x = 300
    const y = 500
    const lineWidth = 30
    
    const sideAngle = Math.PI * 0.05
    const topWidthDiff = Math.sin(sideAngle) * height / 2
    const corners: ICornerPoints = {
      topLeft: Matter.Vector.create(x - width / 2 - topWidthDiff, y - height),
      topRight: Matter.Vector.create(x + width / 2 + topWidthDiff, y - height),
      bottomLeft: Matter.Vector.create(x - width / 2 + lineWidth, y - lineWidth),
      bottomRight: Matter.Vector.create(x + width / 2 - lineWidth, y - lineWidth)
    }

    const sideLeft = Matter.Bodies.rectangle(x - width / 2 - topWidthDiff / 2, y - height / 2, lineWidth, height, { isStatic: true, angle: -sideAngle, restitution: 0.0001, density: 100 })
    const sideRight = Matter.Bodies.rectangle(x + width / 2 + topWidthDiff / 2, y - height / 2, lineWidth, height, { isStatic: true, angle: sideAngle, restitution: 0.0001, density: 100 })
    const topLeft = Matter.Bodies.rectangle(corners.topLeft.x, corners.topLeft.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })
    const topRight = Matter.Bodies.rectangle(corners.topRight.x, corners.topRight.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })
    const bottomLeft = Matter.Bodies.rectangle(corners.bottomLeft.x, corners.bottomLeft.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })
    const bottomRight = Matter.Bodies.rectangle(corners.bottomRight.x, corners.bottomLeft.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })
    const waterLeftPos = this.posBetween(corners.bottomLeft, corners.topLeft, this._waterLevel)
    const waterRightPos = this.posBetween(corners.bottomRight, corners.topRight, this._waterLevel)
    const waterLeft = Matter.Bodies.rectangle(waterLeftPos.x, waterLeftPos.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })
    const waterRight = Matter.Bodies.rectangle(waterRightPos.x, waterRightPos.y, 1, 1, { isStatic: true, restitution: 0.0001, density: 100 })

    const cap = Matter.Bodies.rectangle(
      corners.topLeft.x + (width + topWidthDiff) / 2,
      corners.topLeft.y,
      width + topWidthDiff - 10,
      lineWidth,
      {
        isStatic: true,
        restitution: 0.0001,
        density: 100,
      }
    )

    this.cupCorners = {
      topLeft: topLeft,
      topRight: topRight,
      bottomLeft: bottomLeft,
      bottomRight: bottomRight
    }
    this._waterLeftEdge = waterLeft
    this._waterRightEdge = waterRight

    const cup = this.cup = Matter.Body.create({
      parts: [
        topLeft,
        topRight,
        waterLeft,
        waterRight,
        bottomLeft,
        bottomRight,
        sideLeft,
        sideRight,
        Matter.Bodies.rectangle(x, y - lineWidth / 2, width + lineWidth, lineWidth, { isStatic: true,friction: 1, restitution: 0, density: 10000 }),
        //Matter.Bodies.rectangle(x, y - height + 80 / 2, width + lineWidth, 20, { isStatic: true,friction: 0.95, restitution: 0.8, density: 10 })
      ],
      density: 10000,
      restitution: 0
    })
    Matter.World.add(world, cup)

    this.rope = new MatterRope(world, waterLeft, waterRight)
  }

  public get topLeft(): Matter.Vector { return this.cupCorners.topLeft.position }
  public get topRight(): Matter.Vector { return this.cupCorners.topRight.position }
  public get edgeLeft(): Matter.Vector { return this.posBetween(this.cupCorners.bottomLeft.position, this.cupCorners.topLeft.position, this._waterLevel) }
  public get edgeRight(): Matter.Vector { return this.posBetween(this.cupCorners.bottomRight.position, this.cupCorners.topRight.position, this._waterLevel) }
  public get bottomLeft(): Matter.Vector { return this.cupCorners.bottomLeft.position }
  public get bottomRight(): Matter.Vector { return this.cupCorners.bottomRight.position }
  
  public get points(): Matter.Vector[] {
    const firstP = Matter.Vector.create(this.edgeRight.x + 5, this.edgeRight.y - 10)
    const lastP = Matter.Vector.create(this.edgeLeft.x - 5, this.edgeLeft.y - 10)
    return [
      ...this.rope.points,
      firstP,
      this.edgeRight,
      this.bottomRight,
      this.bottomLeft,
      this.edgeLeft,
      lastP
    ]
  }

  /**
   * Get current water level. 0.0 means no water and 1.0 means filled.
   * @return water level 0 .. 1
   */
  public get waterLevel(): number { return this._waterLevel }

  /**
   * Return water level as actual position (Y-axis).
   */
  public get waterTop(): number {
    return this._waterLeftEdge.position.y
  }

  /**
   * Change water level. 0.0 means no water and 1.0 means filled.
   */
  public set waterLevel(v: number) {
    this._waterLevel = v
    this._waterLeftEdge.position = this.posBetween(this.cupCorners.bottomLeft.position, this.cupCorners.topLeft.position, this._waterLevel)
    this._waterRightEdge.position = this.posBetween(this.cupCorners.bottomRight.position, this.cupCorners.topRight.position, this._waterLevel)
  }

  /**
   * return new vector exists on the line v1 to v2.
   * @param v1 point1
   * @param v2 point2
   * @param r amount of point1. 0 = point1, 0.5 = mid of the points.
   */
  private posBetween(v1: Matter.Vector, v2: Matter.Vector, r: number) {
    return Matter.Vector.create(
      v1.x * (1 - r) + v2.x * r,
      v1.y * (1 - r) + v2.y * r
    )
  }

  public capCap() {
    if(this.isCaped) { return }
    this.isCaped = true
    this.rope.scale(3)
  }

}
